// One-time native shell setup. Called from main.tsx on boot.
// Configures status bar style and hardware back button so the Capacitor APK
// feels like a true native app instead of a web wrapper.
//
// ⚠️ Android 15 (API 35) edge-to-edge migration:
// `StatusBar.setBackgroundColor()` and `StatusBar.setOverlaysWebView()` wrap
// `Window.setStatusBarColor` / `setDecorFitsSystemWindows`, which Google Play
// flagged as deprecated. On Android 15 (targetSdk 35) every app is forced
// into edge-to-edge mode, so those calls are NO-OPS and trigger the Play
// Console warning. We now ONLY set the icon style (light/dark icons) and
// rely on:
//   1. The Android theme (`styles.xml`) for the system bar background.
//   2. CSS `env(safe-area-inset-*)` for content padding (already in use
//      across MobileShell, AppLayout bottom nav, etc).
import { isNativeApp } from "./platform";

/**
 * Configure the native shell once on app boot. Safe to call on web — every
 * branch is gated by `isNativeApp()`.
 */
export async function initNativeShell(): Promise<void> {
  if (!isNativeApp()) return;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    // Only set the icon style. Do NOT call setBackgroundColor or
    // setOverlaysWebView — both are deprecated in Android 15 edge-to-edge
    // and flagged by Google Play. The native theme handles the bar color
    // and edge-to-edge insets.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {
    /* StatusBar plugin missing or unsupported — non-fatal */
  }
}

/**
 * Wire the Android hardware back button to React Router history instead of
 * letting the OS close the app from any screen. Called once from <App />.
 *
 * Behaviour (Android best practice):
 *   1. If a Radix-style overlay is open (dialog, sheet, popover, dropdown),
 *      close it first — never navigate behind it.
 *   2. Otherwise, walk router history if we can.
 *   3. On the root with nothing to pop, exit the app.
 *
 * Returns a cleanup function for React useEffect.
 */
export function registerAndroidBackButton(
  navigate: (delta: number) => void,
  exitOnRoot: boolean = true,
): () => void {
  if (typeof window === "undefined") return () => undefined;

  let listenerHandle: { remove: () => Promise<void> } | undefined;

  (async () => {
    try {
      const { Capacitor } = await import("@capacitor/core");
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "android") return;
      const { App: CapApp } = await import("@capacitor/app");
      listenerHandle = await CapApp.addListener("backButton", ({ canGoBack }) => {
        const openOverlay = document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
        );
        if (openOverlay) {
          const closeBtn = openOverlay.querySelector<HTMLElement>(
            'button[aria-label="Close"], [data-radix-dialog-close]',
          );
          if (closeBtn) {
            closeBtn.click();
          } else {
            document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
          }
          return;
        }

        if (canGoBack) {
          navigate(-1);
        } else if (exitOnRoot) {
          CapApp.exitApp();
        }
      });
    } catch {
      /* @capacitor/app missing — non-fatal */
    }
  })();

  return () => {
    listenerHandle?.remove().catch(() => undefined);
  };
}
